import React from "react";
import { Chart } from "react-google-charts";
import "../../chartProperty.scss";
import { IComboChartData } from "./IComboChartData";
import { processDoubleColumnChartData } from "../DoubleColumnChart/DoubleColumnChartFunctions";
import { processComboChartColors } from "./ComboChartFunctions";
import { GoogleComboChartDefaultOptions, GoogleComboChartNoAnimationDefaultOptions } from "../../../../Constants/Chart/GoogleChartDefaultOptions";
import { ResultWithValue } from "../../../../Constants/Result/ResultWithValue";
import { RenderTabOnFocus } from "../../../../Events/RenderTabOnFocus";
import { SmallLoading } from "../../../Loading/IntermediateLoading";
import { RenderFromServiceCall } from "../RenderFromServiceCall";
import { columnSegmentColumnChart } from "../../../../../../Helpers/Chart/chartSegmentClickHelper";

interface IProps {
  height?: string;
  fetchData: () => Promise<ResultWithValue<IComboChartData>>;
  onSegmentClick?: (label: string) => void;
  hash?: string;
  isStacked?: boolean;
  firstChartType?:string;
  firstChartSeriesStart: number;
  secondChartType?: string;
  secondChartSeriesStart: number;
  thirdChartType?: string;
  thirdChartSeriesStart: number;
  chartOptions?: any;
  isEEPlanTracking: boolean;
  clickableSeriesIndex?: number;
}

/**
 * Combo Chart 
 * Chart is used to show 2 different graphs in one. For example a bar chart and line chart on one graph. 
 * Used to show a stepped area chart and bar chart for SD Limitation 
 * 
 **/
export const ComboChart: React.FC<IProps> = (props: IProps) => {
  return (
    <RenderFromServiceCall
      key={props.hash ?? "ComboChart"}
      fetchData={props.fetchData}
      renderFunction={(data: IComboChartData) => {
        const chartData = processDoubleColumnChartData(data, props.isEEPlanTracking);

        const projectionData = data.datasets[props.thirdChartSeriesStart]?.data;

        let projectionColor = "#42a30a"; 

        if (projectionData && projectionData.length > 1) {
          const firstValue = projectionData[0];
          const lastValue = projectionData[projectionData.length - 1];
          projectionColor = lastValue >= firstValue ? "#42a30a" : "#ea4335"; 
        }

        const googleChartOptions =
          window.location.href.includes("disableAnimation")
            ? GoogleComboChartDefaultOptions
            : props.chartOptions?.isPowerPointPrint
            ? GoogleComboChartNoAnimationDefaultOptions
            : GoogleComboChartDefaultOptions;

        return (
          <RenderTabOnFocus>
            <Chart
              width="99%"
              chartType="ComboChart"
              loader={<SmallLoading />}
              height={props.height ?? "375px"}
              data={chartData}
              options={{
                ...googleChartOptions,
                isStacked: props.isStacked,
                seriesType: "bars",
                areaOpacity: 0.0,
                chartArea: {
                  left: 55,
                  right: 0
                },
                series: {
                  [props.firstChartSeriesStart]: {
                    type: props.firstChartType ?? "bars",
                    curveType: "function",
                    color: "#4285F4",
                  },
                  [props.secondChartSeriesStart]: {
                    type: props.secondChartType ?? "steppedArea",
                    color: "#FBBC05", 
                  },
                  [props.thirdChartSeriesStart]: {
                    type: props.thirdChartType ?? "line",
                    color: projectionColor,
                  },
                },
                vAxis: {
                  viewWindow: {
                    min: 0,
                  },
                },
                hAxis: {
                  slantedText: true,
                  slantedTextAngle: 45,
                },
              }}
              rootProps={{ "data-testid": "1" }}
              chartEvents={[
                {
                  eventName: "select",
                  callback: ({ chartWrapper }: { chartWrapper: any }) => {
                    const chart = chartWrapper.getChart();
                    const selection = chart.getSelection();

                    if (!selection.length || !props.onSegmentClick) return;

                    const { row, column } = selection[0];

                    if (props.clickableSeriesIndex === undefined || column === props.clickableSeriesIndex + 1) {
                      columnSegmentColumnChart(data.labels, props.onSegmentClick, chartData)({ chartWrapper });
                    }
                  },
                },
              ]}
              legendToggle
            />
          </RenderTabOnFocus>
        );
      }}
    />
  );
};
